import { getFutureDate } from '../../../../../utils/dateFunctions';
import {
    getCharMinMessage,
    getDateMinOrSameMessage,
    getInvalidMessage,
    getNowDateOrEarlierMessage,
    getNumMaxMessage,
    getReqMessage,
    getSameDateOrLater,
    getSelectMessage
} from '../../../../../utils/formValidation/errorMessageFunctions';
import {
    validatePhone,
    validatePostcode
} from '../../../../../utils/formValidation/validationFunctions';
import { regExps } from '../../../../../utils/formValidation/validationPatterns';
import { validateLastName } from '../../../validation/validationFunctions';

const MAX_HOURS_PER_WEEK = 48;
const MAX_HOURLY_PAY = 999.99;
const MIN_IWS_CALL_NOTES = 10;

export const validateEmploymentTypeId = (employmentTypeId, errors) => {
    if (!employmentTypeId) {
        return {
            ...errors,
            employmentTypeId: { error: true, message: getSelectMessage('employment type', 'an') }
        };
    }
    return errors;
};

export const validateSelfEmploymentClaimCalculationDate = (
    selfEmploymentClaimCalculationDate,
    errors,
    startDate
) => {
    if (!selfEmploymentClaimCalculationDate) {
        return {
            ...errors,
            selfEmploymentClaimCalculationDate: {
                error: true,
                message: getReqMessage('Self employed claim calculation date')
            }
        };
    } else if (selfEmploymentClaimCalculationDate < startDate) {
        return {
            ...errors,
            selfEmploymentClaimCalculationDate: {
                error: true,
                message: getDateMinOrSameMessage('start date')
            }
        };
    }
    return errors;
};

export const validateStartDate = (startDate, errors, allowFutureDates = false) => {
    if (!startDate) {
        return {
            ...errors,
            startDate: { error: true, message: getReqMessage('Start date') }
        };
    } else if (getFutureDate(startDate) === true && !allowFutureDates) {
        return {
            ...errors,
            startDate: { error: true, message: getNowDateOrEarlierMessage() }
        };
    }
    return errors;
};

export const validateEndDate = (endDate, startDate, selfEmploymentClaimCalculationDate, errors) => {
    if (startDate > endDate) {
        return {
            ...errors,
            endDate: { error: true, message: getSameDateOrLater('start date') }
        };
    } else if (selfEmploymentClaimCalculationDate && selfEmploymentClaimCalculationDate > endDate) {
        return {
            ...errors,
            endDate: {
                error: true,
                message: getSameDateOrLater('self employed claim calculation date')
            }
        };
    } else if (getFutureDate(endDate) === true) {
        return {
            ...errors,
            endDate: {
                error: true,
                message: getNowDateOrEarlierMessage()
            }
        };
    }
    return errors;
};

export const validateEndStatusId = (endStatusId, errors) => {
    if (!endStatusId) {
        return {
            ...errors,
            endStatusId: { error: true, message: getReqMessage('End status') }
        };
    }
    return errors;
};

export const validateStartStatusId = (startStatusId, errors) => {
    if (!startStatusId) {
        return {
            ...errors,
            startStatusId: { error: true, message: getReqMessage('Start status') }
        };
    }
    return errors;
};

export const validateVacancyTitle = (vacancyTitle, errors) => {
    if (!vacancyTitle) {
        return {
            ...errors,
            vacancyTitle: { error: true, message: getReqMessage('Vacancy title') }
        };
    }
    return errors;
};

export const validateHourlyPay = (hourlyPay, errors) => {
    if (!hourlyPay || hourlyPay < 1) {
        return {
            ...errors,
            hourlyPay: { error: true, message: getReqMessage('Hourly pay') }
        };
    } else if (hourlyPay > MAX_HOURLY_PAY) {
        return {
            ...errors,
            hourlyPay: { error: true, message: getNumMaxMessage('Hourly pay', MAX_HOURLY_PAY) }
        };
    } else if (!regExps.PAY_R.test(hourlyPay)) {
        return {
            ...errors,
            hourlyPay: { error: true, message: getInvalidMessage('Value') }
        };
    }
    return errors;
};

export const validateHoursPerWeek = (hoursPerWeek, hoursPerWeekId, errors, fixedFlexibleHour) => {
    if (fixedFlexibleHour === 'fixed') {
        if (!hoursPerWeek || hoursPerWeek < 1) {
            return {
                ...errors,
                hoursPerWeek: { error: true, message: getReqMessage('Hours per week') }
            };
        } else if (hoursPerWeek > MAX_HOURS_PER_WEEK) {
            return {
                ...errors,
                hoursPerWeek: {
                    error: true,
                    message: getNumMaxMessage('Hours per week', MAX_HOURS_PER_WEEK)
                }
            };
        } else if (!regExps.PAY_R.test(hoursPerWeek)) {
            return {
                ...errors,
                hoursPerWeek: { error: true, message: getInvalidMessage('Value') }
            };
        } else return errors;
    }
    if (fixedFlexibleHour === 'flexible') {
        if (!hoursPerWeekId) {
            return {
                ...errors,
                hoursPerWeekId: {
                    error: true,
                    message: getSelectMessage('hours per week value', 'an')
                }
            };
        }
        return errors;
    }
};

export const validateJobSectorId = (jobSectorId, errors) => {
    if (!jobSectorId) {
        return {
            ...errors,
            jobSectorId: { error: true, message: getSelectMessage('job sector') }
        };
    }
    return errors;
};

export const validateBusinessName = (businessName, errors) => {
    if (!businessName) {
        return {
            ...errors,
            businessName: { error: true, message: getReqMessage('Business name') }
        };
    }
    return errors;
};

export const validateLeadGeneratedById = (leadGeneratedById, errors) => {
    if (!leadGeneratedById) {
        return {
            ...errors,
            leadGeneratedById: {
                error: true,
                message: getSelectMessage('either Participant or Adviser')
            }
        };
    }
    return errors;
};

export const validateLeadAdviserId = (leadAdviserId, errors) => {
    if (!leadAdviserId) {
        return {
            ...errors,
            leadAdviserId: { error: true, message: getSelectMessage('adviser', 'an') }
        };
    }
    return errors;
};

export const validateHiringManagerLastName = (lastName, errors) => {
    if (!lastName) {
        return {
            ...errors,
            lastName: { error: true, message: getReqMessage('Last name') }
        };
    } else return validateLastName(lastName, errors);
};

export const validateHiringManagerOfficeNumber = (hiringManagerOfficeNumber, errors) => {
    if (!hiringManagerOfficeNumber) {
        return {
            ...errors,
            hiringManagerOfficeNumber: { error: true, message: getReqMessage('Phone number') }
        };
    } else return validatePhone(hiringManagerOfficeNumber, errors, 'hiringManagerOfficeNumber');
};

export const validateAddressLine1 = (addressLine1, errors) => {
    if (!addressLine1) {
        return {
            ...errors,
            addressLine1: { error: true, message: getReqMessage('Address line 1') }
        };
    }
    return errors;
};

export const validateCity = (city, errors) => {
    if (!city) {
        return {
            ...errors,
            city: { error: true, message: getReqMessage('City') }
        };
    }
    return errors;
};

export const validateBusinessPostcode = (postcode, errors) => {
    if (!postcode) {
        return {
            ...errors,
            postcode: { error: true, message: getReqMessage('Postcode') }
        };
    } else return validatePostcode(postcode, errors);
};

export const validateCallOutcomeId = (callOutcomeId, errors) => {
    if (!callOutcomeId) {
        return {
            ...errors,
            callOutcomeId: { error: true, message: getSelectMessage('call outcome') }
        };
    }
    return errors;
};

export const validateWorkConfidenceId = (workConfidenceId, errors) => {
    if (!workConfidenceId) {
        return {
            ...errors,
            workConfidenceId: { error: true, message: getSelectMessage('work confidence') }
        };
    }
    return errors;
};

export const validateIwsCallNotes = (note, errors) => {
    if (!note) {
        return {
            ...errors,
            note: {
                error: true,
                message: getReqMessage('A note')
            }
        };
    } else if (note.length < MIN_IWS_CALL_NOTES) {
        return {
            ...errors,
            note: {
                error: true,
                message: getCharMinMessage('Notes', MIN_IWS_CALL_NOTES)
            }
        };
    }
    return errors;
};

export const validateFrequencyId = (frequencyId, errors) => {
    if (!frequencyId) {
        return {
            ...errors,
            frequencyId: { error: true, message: getSelectMessage('frequency') }
        };
    }
    return errors;
};

export const validateIwsEventOwnerId = (iwsEventOwnerId, errors) => {
    if (!iwsEventOwnerId) {
        return {
            ...errors,
            iwsEventOwnerId: { error: true, message: getSelectMessage('iwsEventOwnerId') }
        };
    }
    return errors;
};
